import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { CheckCircle2, Mail } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import {
  IMAGES,
  PROVINCES,
  FRANCHISE_WHY,
  FRANCHISE_INCLUDED,
  FRANCHISE_INVESTMENT,
} from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const scrollToForm = () => {
  document.getElementById("franchise-contact")?.scrollIntoView({ behavior: "smooth" });
};

const ctaBtn =
  "inline-flex items-center justify-center gap-2 rounded-[5px] bg-[#F8B808] px-7 h-10 font-body text-base font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98]";

const FranchiseHero = () => (
  <section className="pt-[118px] lg:pt-40 pb-[40px] lg:pb-[64px] bg-[#FFFFFF]" data-testid="franchise-hero">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#1A1A1A] leading-[1.04]">
            Franchise Opportunities
          </h1>
          <p className="font-body text-lg md:text-xl text-[#6B6B66] mt-6 lg:mt-[39px] leading-relaxed max-w-lg">
            Bring the Home Cart experience to your community. Discover a proven,
            value-driven retail model backed{" "}
            <br className="hidden lg:block" />
            by trusted brands and hands-on support.
          </p>
          <button
            onClick={scrollToForm}
            data-testid="franchise-hero-cta"
            className={`${ctaBtn} mt-8`}
          >
            Request Franchise Information
          </button>
        </div>
        <Reveal>
          <div className="overflow-hidden rounded-xl aspect-[5/4]">
            <img
              src="https://customer-assets.emergentagent.com/job_discount-retail-hub-1/artifacts/vmgqsrqe_cb48db6c-9c7f-41e5-b89a-73f72287bffd.png"
              alt="A Home Cart associate helping a customer at the store counter"
              className="h-full w-full object-cover"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const WhyHomeCart = () => (
  <section className="py-[35px] lg:py-[64px] bg-white" data-testid="franchise-why">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <h2 className="font-heading text-[31px] sm:text-4xl lg:text-[44px] font-medium tracking-tight text-[#1A1A1A] leading-[1.06]">
          A model built to last
        </h2>
      </div>
      <div className="mt-9 lg:mt-[51px] grid md:grid-cols-3 gap-6 lg:gap-8">
        {FRANCHISE_WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.08}>
            <div
              data-testid={`franchise-why-${i}`}
              className="h-full rounded-[18px] bg-[#FFFFFF] border border-[#E8E8E8] py-8 px-[24px] sm:p-8"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FBF4DD]">
                <Icon name={w.icon} className="h-6 w-6 text-[#1A1A1A]" />
              </span>
              <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mt-6">{w.title}</h3>
              <p className="font-body text-base text-[#6B6B66] mt-3 leading-relaxed">{w.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhatsIncluded = () => (
  <section className="py-[35px] lg:py-[64px] bg-[#FFFFFF]" data-testid="franchise-included">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="max-w-2xl text-left lg:mx-auto lg:text-center">
        <h2 className="font-heading text-[31px] sm:text-4xl lg:text-[44px] font-medium tracking-tight text-[#1A1A1A] leading-[1.06]">
          Everything you need{" "}
          <br className="sm:hidden" />to launch
        </h2>
      </div>
      <div className="mt-10 lg:mt-[55px] grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-[28px] sm:gap-y-10">
        {FRANCHISE_INCLUDED.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.05}>
            <div data-testid={`franchise-included-${i}`} className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FBF4DD]">
                <Icon name={f.icon} className="h-5 w-5 text-[#1A1A1A]" />
              </span>
              <h3 className="font-heading text-base font-semibold text-[#1A1A1A] leading-snug">{f.title}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const InvestmentOverview = () => (
  <section className="py-[35px] lg:py-[64px] bg-white" data-testid="franchise-investment">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="max-w-2xl">
        <h2 className="font-heading text-[31px] sm:text-4xl lg:text-[44px] font-medium tracking-tight text-[#1A1A1A] leading-[1.06]">
          A transparent starting point
        </h2>
        <p className="font-body text-base text-[#6B6B66] mt-5 lg:mt-[40px] leading-relaxed">
          Figures below are indicative. Contact us for{" "}
          <br className="sm:hidden" />a tailored investment overview.
        </p>
      </div>
      <div className="mt-10 rounded-xl bg-white border border-[#E8E8E8] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
        <div className="flex items-center justify-between gap-6 px-7 sm:px-9 py-5 bg-[#FFFFFF] border-b border-[#E8E8E8]">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.16em] text-[#9A958C]">Investment Item</span>
          <span className="font-body text-xs font-semibold uppercase tracking-[0.16em] text-[#9A958C]">Details</span>
        </div>
        {FRANCHISE_INVESTMENT.map((row, i) => (
          <div
            key={row.item}
            data-testid={`franchise-stat-${i}`}
            className={`flex items-center justify-between gap-8 px-7 sm:px-9 py-7 ${
              i !== 0 ? "border-t border-[#F0EEE9]" : ""
            }`}
          >
            <span className="font-heading text-base sm:text-lg font-semibold text-[#1A1A1A]">{row.item}</span>
            <span className="font-body text-sm sm:text-base text-[#6B6B66] text-right">{row.detail}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const inputClass =
  "w-full rounded-lg border border-[#D6D6D6] bg-white px-4 py-3.5 font-body text-sm text-[#1A1A1A] placeholder:text-[#B5B0A6] outline-none transition-colors focus:border-[#F8B808]";

const FranchiseContact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", province: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, inquiry_type: "franchise", source: "Franchise Page Form" });
      setDone(true);
      toast.success("Thank you! Our franchise team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", province: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="franchise-contact" className="py-[35px] lg:py-[72px] bg-[#FFFFFF]" data-testid="franchise-contact">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-28">
            <h2 className="font-heading text-[31px] sm:text-5xl lg:text-[50px] font-medium tracking-tight text-[#1A1A1A] leading-[1.04]">
              Ready to Learn More?
            </h2>
            <p className="font-body text-lg md:text-xl text-[#6B6B66] mt-6 lg:mt-[34px] leading-relaxed max-w-lg">
              Tell us about yourself and your market.{" "}
              <br className="sm:hidden" />Our franchise team will
              reach out with the details you need.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FBF4DD]">
                <Mail className="h-5 w-5 text-[#1A1A1A]" />
              </span>
              <div>
                <p className="font-body text-xs font-semibold uppercase tracking-wide text-[#9A958C]">Email</p>
                <p className="font-heading text-base font-semibold text-[#1A1A1A]">info@homecart.ca</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white border border-[#E8E8E8] p-7 sm:p-10 shadow-[0_12px_44px_rgba(0,0,0,0.04)]">
            {done ? (
              <div className="flex flex-col items-center text-center py-16" data-testid="franchise-success">
                <CheckCircle2 className="h-16 w-16 text-[#F8B808]" />
                <h3 className="font-heading text-2xl font-bold text-[#1F2937] mt-5">Request Received</h3>
                <p className="font-body text-[#6B7280] mt-3 max-w-sm">
                  Thank you for your interest in Home Cart. Our franchise team will get back to you soon.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="franchise-send-another"
                  className="mt-7 font-body text-sm font-bold text-[#F8B808] hover:underline"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="franchise-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Name</label>
                    <input
                      data-testid="franchise-input-name"
                      className={inputClass}
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Email</label>
                    <input
                      data-testid="franchise-input-email"
                      type="email"
                      className={inputClass}
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Phone</label>
                    <input
                      data-testid="franchise-input-phone"
                      className={inputClass}
                      placeholder="(000) 000-0000"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Province</label>
                    <select
                      data-testid="franchise-input-province"
                      className={`${inputClass} appearance-none`}
                      value={form.province}
                      onChange={(e) => update("province", e.target.value)}
                    >
                      <option value="">Select a province</option>
                      {PROVINCES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-semibold text-[#1F2937] mb-1.5 block">Message</label>
                  <textarea
                    data-testid="franchise-input-message"
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us about your market and your interest in Home Cart..."
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="franchise-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-[5px] bg-[#F8B808] px-7 h-10 font-body text-base font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98] disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Request Franchise Information"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Franchise() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#FFFFFF]">
      <Header />
      <main>
        <FranchiseHero />
        <WhyHomeCart />
        <WhatsIncluded />
        <InvestmentOverview />
        <FranchiseContact />
      </main>
      <Footer />
    </div>
  );
}
